import React, { useRef, useState, useEffect, useCallback } from "react";
import { XIcon, RotateCcw } from "lucide-react";
import { toast } from "react-hot-toast";

const PermitSignatureModal = ({
  isOpen,
  onClose,
  onSignatureSuccess,
  onSuccess,
  actionTitle = "Submit Action",
  consentConfig,
}) => {
  const canvasRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const [signatureSubmitting, setSignatureSubmitting] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const hasDrawnRef = useRef(false);

  const resizeSignatureCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = canvasWrapRef.current;

    if (!canvas || !wrapper) return;

    const previousDataUrl =
      hasDrawnRef.current && canvas.width && canvas.height
        ? canvas.toDataURL("image/png")
        : null;

    const rect = wrapper.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    const width = Math.max(rect.width, 300);
    const height = Math.max(rect.height, 260);

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#111827";

    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    if (previousDataUrl) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = previousDataUrl;
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    hasDrawnRef.current = false;
    setConsentAccepted(false);

    const timer = setTimeout(() => {
      resizeSignatureCanvas();
    }, 80);

    window.addEventListener("resize", resizeSignatureCanvas);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", resizeSignatureCanvas);
    };
  }, [isOpen, resizeSignatureCanvas]);

  if (!isOpen) return null;

  const getCanvasPoint = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();

    let clientX;
    let clientY;

    if (event.touches && event.touches.length > 0) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else if (event.changedTouches && event.changedTouches.length > 0) {
      clientX = event.changedTouches[0].clientX;
      clientY = event.changedTouches[0].clientY;
    } else {
      clientX = event.clientX;
      clientY = event.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  const startDrawing = (event) => {
    event.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const point = getCanvasPoint(event);

    if (!ctx || !point) return;

    canvas.setPointerCapture?.(event.pointerId);

    ctx.beginPath();
    ctx.moveTo(point.x, point.y);

    setIsDrawing(true);
  };

  const draw = (event) => {
    if (!isDrawing) return;

    event.preventDefault();

    const ctx = canvasRef.current?.getContext("2d");
    const point = getCanvasPoint(event);

    if (!ctx || !point) return;

    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    hasDrawnRef.current = true;
  };

  const stopDrawing = (event) => {
    event?.preventDefault?.();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (ctx) {
      ctx.closePath();
    }

    if (canvas && event?.pointerId !== undefined) {
      canvas.releasePointerCapture?.(event.pointerId);
    }

    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const wrapper = canvasWrapRef.current;
    const rect = wrapper ? wrapper.getBoundingClientRect() : canvas.getBoundingClientRect();

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, rect.width, rect.height);

    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.restore();

    hasDrawnRef.current = false;
  };

  const handleSubmit = async () => {
    if (!hasDrawnRef.current || !canvasRef.current) {
      toast.error("Please provide your signature.");
      return;
    }

    if (consentConfig?.enabled && !consentAccepted) {
      toast.error("Please accept the consent checkbox before continuing.");
      return;
    }

    const dataUrl = canvasRef.current.toDataURL("image/png");
    const base64Part = dataUrl.split(",")[1] || "";

    if (!base64Part) {
      toast.error("Could not capture signature. Try again.");
      return;
    }

    setSignatureSubmitting(true);

    try {
      const callback = onSignatureSuccess || onSuccess;
      if (typeof callback === "function") {
        callback(dataUrl, {
          consent_accepted: consentConfig?.enabled ? consentAccepted : false,
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Failed to submit signature.");
    } finally {
      setSignatureSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="w-[95vw] max-w-2xl rounded-2xl bg-white p-5 shadow-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">
            Provide Signature for {actionTitle}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={signatureSubmitting}
            className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-3 text-xs text-slate-500">
          Draw your signature below and submit to complete this action.
        </p>

        <div
          ref={canvasWrapRef}
          className="h-[240px] w-full rounded-xl border border-slate-300 bg-white sm:h-[280px] md:h-[300px] overflow-hidden"
        >
          <canvas
            ref={canvasRef}
            className="h-full w-full rounded-xl bg-white touch-none cursor-crosshair"
            style={{ touchAction: "none" }}
            onPointerDown={startDrawing}
            onPointerMove={draw}
            onPointerUp={stopDrawing}
            onPointerCancel={stopDrawing}
            onPointerLeave={stopDrawing}
          />
        </div>

        {consentConfig?.enabled && (
          <div className="mt-3 rounded-xl border border-orange-200 bg-orange-50 p-3 shadow-xs">
            <label className="flex items-start gap-2.5 text-xs font-medium text-slate-800 cursor-pointer">
              <input
                type="checkbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-orange-300 text-orange-600 focus:ring-orange-500 cursor-pointer"
              />
              <span className="leading-snug text-slate-700">{consentConfig.text}</span>
            </label>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={clearSignature}
            disabled={signatureSubmitting}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer"
          >
            <RotateCcw className="h-4 w-4" />
            Clear
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={signatureSubmitting}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={signatureSubmitting}
              className="rounded-xl bg-orange-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 disabled:opacity-60 cursor-pointer"
            >
              {signatureSubmitting ? "Submitting..." : `Confirm ${actionTitle}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermitSignatureModal;
